import React, { useEffect } from 'react'
import { graphql, Link, navigate } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolder } from '@fortawesome/free-solid-svg-icons/faFolder'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight'
import Layout from '../components/Layout'
import Seo from '../components/Seo'
import Breadcrumb from '../components/Breadcrumb'
import { toTitleCase } from '../utils/physiotherapyLinks'
import '../css/blogs.scss'

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function BlogListPage({ data, pageContext }) {
  const {
    category,
    basePath,
    currentPage,
    totalPages,
    totalCount,
    allCount,
    categories,
  } = pageContext

  const blogs = data.allWpPage.edges

  const pagePath = (page) =>
    page === 1 ? `${basePath}/` : `${basePath}/${page}/`
  const currentPath = pagePath(currentPage)
  const categoryLabel = category ? toTitleCase(category) : null

  // The pre-static version of this listing used ?category=&page= query
  // params; forward any such shared/bookmarked URL to its static page.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const categoryParam = params.get('category')
    const pageParam = parseInt(params.get('page'), 10)
    if (!categoryParam && (Number.isNaN(pageParam) || pageParam <= 1)) return

    const match = categoryParam
      ? categories.find((c) => c.name === categoryParam)
      : null
    const base = match ? `/blogs/${match.slug}` : '/blogs'
    const page =
      !Number.isNaN(pageParam) && pageParam > 1 ? `${pageParam}/` : ''
    navigate(`${base}/${page}`, { replace: true })
  }, [categories])

  const pageSuffix = currentPage > 1 ? ` - Page ${currentPage}` : ''
  const seoTitle = categoryLabel
    ? `${categoryLabel} Blogs & Articles${pageSuffix} - Portea Medical`
    : `Healthcare Blogs${pageSuffix} - Portea Medical`
  const seoDescription = categoryLabel
    ? `Read ${totalCount} expert ${categoryLabel} articles — health tips and medical advice from Portea Medical professionals.`
    : 'Read the latest healthcare insights, tips, and expert advice from Portea Medical professionals.'

  const breadcrumbs = [
    { target: '/', display: 'Home', keep: true },
    ...(category
      ? [
          { target: '/blogs', display: 'Blogs', keep: true },
          { target: currentPath, display: categoryLabel },
        ]
      : [{ target: currentPath, display: 'Blogs' }]),
  ]

  const listSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: seoTitle,
    url: `https://www.portea.com${currentPath}`,
    description: seoDescription,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Portea Medical',
      url: 'https://www.portea.com',
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: totalCount,
      itemListElement: blogs.map(({ node: blog }, index) => ({
        '@type': 'ListItem',
        position: (currentPage - 1) * blogs.length + index + 1,
        url: `https://www.portea.com${blog.fields.location}`,
        name: blog.title,
      })),
    },
  }

  return (
    <Layout>
      <Seo
        title={seoTitle}
        description={seoDescription}
        url={currentPath}
        breadcrumbs={breadcrumbs}
        fullSchema={listSchema}
      />

      <Breadcrumb breadcrumbs={breadcrumbs} />

      <div className="blogs-page">
        <header className="blogs-header">
          <div className="container">
            <h1>
              {categoryLabel ? `${categoryLabel} Blogs` : 'Healthcare Blogs'}
            </h1>
            <p>
              Expert insights, health tips, and medical advice from our
              healthcare professionals
            </p>
            <span className="blogs-count">
              {categoryLabel
                ? `${totalCount.toLocaleString()} ${categoryLabel} article${
                    totalCount === 1 ? '' : 's'
                  }`
                : `${allCount.toLocaleString()} articles across ${
                    categories.length
                  } categories`}
            </span>
          </div>
        </header>

        <section className="blogs-body">
          <div className="container">
            <nav
              className="blogs-filterbar"
              aria-label="Browse articles by category"
            >
              <Link
                to="/blogs/"
                className={`category-chip ${!category ? 'active' : ''}`}
                aria-current={!category ? 'page' : undefined}
              >
                All <span className="chip-count">{allCount}</span>
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/blogs/${c.slug}/`}
                  className={`category-chip ${
                    category === c.name ? 'active' : ''
                  }`}
                  aria-current={category === c.name ? 'page' : undefined}
                >
                  {toTitleCase(c.name)}{' '}
                  <span className="chip-count">{c.count}</span>
                </Link>
              ))}
            </nav>

            <div className="results-line">
              <p>
                {totalPages > 1
                  ? `Page ${currentPage} of ${totalPages} · `
                  : ''}
                {totalCount.toLocaleString()} article
                {totalCount === 1 ? '' : 's'}
                {categoryLabel && ` in ${categoryLabel}`}
              </p>
            </div>

            <div className="blogs-grid">
              {blogs.map(({ node: blog }) => {
                const blogImage =
                  blog.sections?.secondaryImage?.localFile?.childImageSharp
                    ?.gatsbyImageData
                const blogCategory = blog.serviceType?.category || 'General'

                return (
                  <article key={blog.id} className="blog-card">
                    <Link to={blog.fields.location} className="blog-card-link">
                      <div className="blog-card-image">
                        {blogImage ? (
                          <GatsbyImage
                            image={blogImage}
                            alt={blog.title}
                            className="blog-image"
                          />
                        ) : (
                          <div className="blog-placeholder">
                            <FontAwesomeIcon icon={faFolder} />
                          </div>
                        )}
                        <span className="blog-category-badge">
                          {toTitleCase(blogCategory)}
                        </span>
                      </div>
                      <div className="blog-card-content">
                        <h2 className="blog-card-title">{blog.title}</h2>
                        {blog.date && (
                          <p className="blog-date">{formatDate(blog.date)}</p>
                        )}
                        {blog.seo?.metaDesc && (
                          <p className="blog-excerpt">{blog.seo.metaDesc}</p>
                        )}
                      </div>
                    </Link>
                  </article>
                )
              })}
            </div>

            {totalPages > 1 && (
              <nav className="pagination" aria-label="Blog pagination">
                {currentPage > 1 ? (
                  <Link
                    to={pagePath(currentPage - 1)}
                    className="pagination-link"
                    rel="prev"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                    Previous
                  </Link>
                ) : (
                  <span
                    className="pagination-link is-disabled"
                    aria-disabled="true"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                    Previous
                  </span>
                )}

                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, i) => {
                    const pageNumber = i + 1
                    const isCurrentPage = pageNumber === currentPage

                    // Show first page, last page, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 &&
                        pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <Link
                          key={pageNumber}
                          to={pagePath(pageNumber)}
                          className={`pagination-number ${
                            isCurrentPage ? 'active' : ''
                          }`}
                          aria-current={isCurrentPage ? 'page' : undefined}
                        >
                          {pageNumber}
                        </Link>
                      )
                    }
                    if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return (
                        <span key={pageNumber} className="pagination-ellipsis">
                          …
                        </span>
                      )
                    }
                    return null
                  })}
                </div>

                {currentPage < totalPages ? (
                  <Link
                    to={pagePath(currentPage + 1)}
                    className="pagination-link"
                    rel="next"
                  >
                    Next
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Link>
                ) : (
                  <span
                    className="pagination-link is-disabled"
                    aria-disabled="true"
                  >
                    Next
                    <FontAwesomeIcon icon={faChevronRight} />
                  </span>
                )}
              </nav>
            )}
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default BlogListPage

export const query = graphql`
  query BlogListById($ids: [String!]) {
    allWpPage(filter: { id: { in: $ids } }, sort: { date: DESC }) {
      edges {
        node {
          id
          title
          date
          seo {
            metaDesc
          }
          fields {
            location
          }
          serviceType {
            category
          }
          sections {
            secondaryImage {
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 480
                    aspectRatio: 1.6
                    quality: 80
                    placeholder: BLURRED
                    layout: CONSTRAINED
                  )
                }
              }
            }
          }
        }
      }
    }
  }
`
